import type { VulnExploit } from '@/lib/types'

export interface ActiveExploitationCardProps {
  exploits: VulnExploit[]
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

/**
 * V6 — Card 1: Active Exploitation.
 *
 * Signals in-the-wild exploitation based on public exploit references pulled
 * by the CIRCL enrichment adapter (Slice 2b). Hidden when the exploit list
 * is empty — the empty state is "no signal", not a placeholder.
 *
 * Visual treatment: destructive border + muted destructive tint, matching the
 * DESIGN.md severity critical/high token family. The card deliberately groups
 * entries by source (ExploitDB, GitHub PoC, etc.) so reviewers can at a glance
 * see whether a single source is noisy vs. multiple sources corroborate.
 */
export function ActiveExploitationCard({
  exploits,
}: ActiveExploitationCardProps) {
  if (exploits.length === 0) return null

  const groups = new Map<string, VulnExploit[]>()
  for (const e of exploits) {
    const key = e.source ?? 'Unknown source'
    const existing = groups.get(key) ?? []
    existing.push(e)
    groups.set(key, existing)
  }
  // Sort each group's items by publishedAt descending.
  for (const [, arr] of groups) {
    arr.sort((a, b) => {
      const da = a.publishedAt ?? ''
      const db = b.publishedAt ?? ''
      return db.localeCompare(da)
    })
  }

  // First seen = earliest publishedAt overall (for the footer summary).
  const firstSeen = exploits
    .map((e) => e.publishedAt)
    .filter((x): x is string => typeof x === 'string' && x.length > 0)
    .sort()[0]

  const countLabel =
    exploits.length === 1
      ? '1 public exploit detected'
      : `${exploits.length} public exploits detected`

  return (
    <section
      data-testid="active-exploitation-card"
      className="rounded-lg border border-destructive bg-destructive/5 p-4 my-4"
      aria-label="Active Exploitation"
    >
      <h3 className="text-sm font-semibold text-destructive">
        ⚠ Active Exploitation
      </h3>
      <p className="text-sm mt-1">{countLabel}</p>
      <ul className="mt-2 space-y-1 text-sm">
        {Array.from(groups.entries()).flatMap(([source, items]) =>
          items.map((e) => (
            <li key={e.id} className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{source}</span>
              <a
                href={e.url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary hover:underline truncate max-w-md"
              >
                {e.url}
              </a>
              {e.publishedAt && (
                <span className="text-muted-foreground text-xs">
                  ({formatDate(e.publishedAt)})
                </span>
              )}
            </li>
          )),
        )}
      </ul>
      {firstSeen && (
        <p className="text-xs text-muted-foreground mt-2">
          First seen: {formatDate(firstSeen)}
        </p>
      )}
    </section>
  )
}
